import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Spinner } from '@forwardflow/ui/Spinner'
import LocalStorage from '@forwardflow/services/LocalStorage'
import {
   readMcpHandle,
   rememberMcpHandle,
   resumeMcpIfPending,
} from '@forwardflow/services/McpContinuation'
import AuthService from '@forwardflow/services/AuthService'
import SocialLogin from '../Signup/SocialLogin'
import { SigninForm } from '@forwardflow/forms/SigninForm'
import { DemoService } from '@forwardflow/services/DemoBookingService'
import { message } from 'antd'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { assistantAuthPath } from '@forwardflow/services/AssistantConnect'

const SigninComponent = () => {
   const [searchParams] = useSearchParams()
   const [socialLogin, setSocialLogin] = useState(false)
   const { navigateUser, getCurrentOrgId } = useUserNavigation()
   const clearSessionData = () => {
      const isTourDone = LocalStorage.get('isTourDone')
      const isCampaignTourDone = LocalStorage.get('isCampaignTourDone')

      LocalStorage.clear()

      if (isTourDone) LocalStorage.put('isTourDone', isTourDone)
      if (isCampaignTourDone)
         LocalStorage.put('isCampaignTourDone', isCampaignTourDone)
   }
   const handleSocialLogin = useCallback(async () => {
      let data: { accessToken: string } | null = null
      const authPlatform = searchParams.get('auth')
      // get temporary token from search params
      const tempToken = searchParams.get('token')
      const demoBookingId = searchParams.get('demoBookingId')
      const isDemoBookingEmailDifferent = searchParams.get(
         'isDemoBookingEmailDifferent'
      )
      const newEmail = searchParams.get('newEmail')
      if (tempToken === null) return

      if (isDemoBookingEmailDifferent) {
         await DemoService.overrideDemoBookingInfo(
            demoBookingId ? parseInt(demoBookingId) : 0,
            { email: newEmail || undefined }
         )
         message.success('Email has been replaced with demo email')
      }

      try {
         setSocialLogin(true)
         if (
            authPlatform === 'google' ||
            authPlatform === 'outlook' ||
            authPlatform === 'microsoft'
         ) {
            data = await AuthService.apiGenerateAccessToken(tempToken)
         } else {
            data = await AuthService.apiValidateEmail(tempToken)
         }
         if (data && data.accessToken) {
            LocalStorage.put('access_token', data.accessToken)

            // The SSO return. The handle was stored before leaving for the
            // provider, because Google and Microsoft return to
            // `window.location.origin` and drop every query parameter.
            if (resumeMcpIfPending()) return

            const currentOrgId = await getCurrentOrgId()
            navigateUser({
               currentOrgId: currentOrgId || '',
               searchParams: location.search,
            })
         }
      } catch (error) {
         console.error('error', error)
      }
   }, [searchParams, navigateUser, getCurrentOrgId])

   useEffect(() => {
      const handleAuthEffect = async () => {
         if (searchParams.toString()) {
            //check  in search params if auth param exists
            if (
               searchParams?.get('auth') === 'google' ||
               searchParams?.get('auth') === 'microsoft' ||
               searchParams?.get('auth') === 'outlook'
            ) {
               handleSocialLogin()
            } else {
               // REMEMBER THE HANDLE BEFORE CLEARING. `clearSessionData` wipes
               // stored state for any non-SSO parameter, which is what dropped
               // the MCP continuation on arrival — the user then logged in and
               // went to their dashboard, with the connection they started
               // silently abandoned.
               const handle = readMcpHandle(searchParams)
               clearSessionData()
               if (handle) rememberMcpHandle(handle)
            }
         }
         // if (LocalStorage.get('access_token')) {
         //    const currentOrgId = await getCurrentOrgId()
         //    navigateUser({
         //       currentOrgId: currentOrgId || '',
         //       searchParams: location.search,
         //    })
         // }
      }
      handleAuthEffect()
   }, [searchParams, navigateUser, getCurrentOrgId, handleSocialLogin])

   return (
      <>
         <div
            className={`flex min-h-screen px-4 lg:px-[30px] flex-col  items-center text-center pt-14 md:pt-28`}
         >
            <a href="/">
               <img
                  width={'165px'}
                  src="/images/fff-logo.png"
                  className="md:absolute left-10 top-10"
                  alt="ff icon"
               />
            </a>
            <Link
               className="bg-white/10 transition-all hover:bg-white/20 w-[200px] h-12 flexc rounded-full md:absolute mt-7 md:mt-0 top-10 right-4 md:right-16"
               to={assistantAuthPath('/signup', searchParams)}
            >
               Sign up
            </Link>
            {/* <h1 className="text-[20px] md:text-[24px] mt-[30px] mb-7 font-medium">
               Level up your Real Estate game with Forward Flow
            </h1>
            <h2 className="text-[12px] md:text-[14px] mb-[54px] text-[#C9C9C9]">
               Sign in to your Forward Flow Account
            </h2> */}
            <h1 className="mt-8 text-[24px] font-medium max-w-[650px] mx-auto mb-10">
               Welcome back to Forward Flow
            </h1>

            {!socialLogin ? (
               <>
                  <div className="w-full flex flex-col max-w-[550px] items-start mb-10">
                     <SigninForm />
                     <div className="flex items-center w-full gap-x-3">
                        <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                        <span className="text-[12px]">OR</span>
                        <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                     </div>
                  </div>
                  <SocialLogin />
               </>
            ) : (
               <Spinner />
            )}
            <div className="my-7 flex justify-between max-w-[450px] w-full">
               <a
                  href="/terms"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Terms and Conditions
               </a>
               <a
                  href="/privacy"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Privacy Policy
               </a>
            </div>
         </div>
      </>
   )
}

export default SigninComponent
